import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, List, ChevronRight } from "lucide-react";
import "./blog-detail.css"; // Giữ nguyên CSS cũ của bạn

const BlogDetail = () => {
  const params = useParams();
  const rawParam = params.slug || params.id;
  const blogId = rawParam ? rawParam.split("-")[0] : null;

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toc, setToc] = useState([]);
  const [processedContent, setProcessedContent] = useState("");

  useEffect(() => {
    if (!blogId) {
      setLoading(false);
      setError("Đường dẫn không hợp lệ.");
      return;
    }

    const fetchBlog = async () => {
      try {
        const response = await axios.get(`https://dht.api.mekongzone.com.vn/api/tintuc/${blogId}`);
        const data = response.data;

        if (!data) throw new Error("Không có dữ liệu");

        setBlog(data);

        if (data.noidung) {
          // --- BƯỚC A: PARSE HTML VÀ TẠO MỤC LỤC PHÂN CẤP ---
          const parser = new DOMParser();
          const doc = parser.parseFromString(data.noidung, 'text/html');

          // Quét cả H2, H3, H4
          const headings = doc.querySelectorAll('h2, h3, h4');
          const extractedToc = [];

          headings.forEach((heading, index) => {
            const slugId = `toc-${index}`;
            heading.id = slugId; // Gắn ID để scroll

            // Xác định level dựa trên tên thẻ
            let level = 1; // Mặc định H2
            if (heading.tagName === 'H3') level = 2;
            if (heading.tagName === 'H4') level = 3;

            extractedToc.push({
              id: slugId,
              title: heading.textContent,
              level: level // 1, 2, hoặc 3
            });
          });

          setToc(extractedToc);

          // Lấy lại HTML đã có ID
          let tempContent = doc.body.innerHTML;

          // --- BƯỚC B: XỬ LÝ ẢNH ---
          let imgIndex = 0;
          tempContent = tempContent.replace(/{{IMAGE_PLACEHOLDER\|?(.*?)}}/g, (match, mode) => {
            const imgUrl = data.hinhanhs && data.hinhanhs[imgIndex] ? data.hinhanhs[imgIndex] : null;
            imgIndex++;
            const displayMode = mode || 'full';

            if (imgUrl) {
              return `
                <figure class="blog-image-container ${displayMode === 'medium' ? 'img-medium' : 'img-full'}">
                  <img src="${imgUrl}" alt="${data.tieude}" loading="lazy" class="blog-detail-image" />
                </figure>
              `;
            } else {
              return "";
            }
          });

          setProcessedContent(tempContent);
        }

      } catch (err) {
        console.error("Lỗi tải blog:", err);
        setError("Không thể tìm thấy bài viết này.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [blogId]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100; // Trừ header
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("vi-VN", {
      year: "numeric", month: "long", day: "numeric"
    });
  };

  if (loading) return <div className="loading-screen">Đang tải nội dung...</div>;
  if (error || !blog) return (
    <div className="container" style={{ textAlign: 'center', marginTop: 50 }}>
      <h2 style={{ color: '#ef4444' }}>{error || "Bài viết không tồn tại"}</h2>
      <Link to="/blogs" className="back-link" style={{ justifyContent: 'center', marginTop: 20 }}>
        <ArrowLeft size={20} /> Quay lại danh sách
      </Link>
    </div>
  );

  return (
    <div className="blog-detail-container">
      <Link to="/blogs" className="back-link">
        <ArrowLeft size={18} /> Quay lại danh sách
      </Link>

      <header className="article-header">
        <h1 className="article-title">{blog.tieude}</h1>
        <div className="article-summary">{blog.mota}</div>
        <div className="article-meta">
          <div className="meta-item"><Calendar size={16} /> {formatDate(blog.ngaydang)}</div>
          <div className="meta-item"><Clock size={16} /> 5 phút đọc</div>
        </div>
      </header>

      {/* MỤC LỤC TỰ ĐỘNG */}
      {toc.length > 0 && (
        <nav className="toc-container">
          <div className="toc-header">
            <List size={20} /> <span>Mục lục nội dung</span>
          </div>
          <div className="toc-content">
            {toc.map((item, index) => (
              <button
                key={index}
                onClick={() => scrollToSection(item.id)}
                className={`toc-item`}
                style={{
                  paddingLeft: `${item.level * 15}px`, // Thụt đầu dòng tự động theo level
                  fontSize: item.level === 1 ? '1rem' : '0.9rem',
                  fontWeight: item.level === 1 ? '600' : '400',
                  color: item.level === 1 ? '#333' : '#666',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  paddingTop: '8px',
                  paddingBottom: '8px',
                  cursor: 'pointer'
                }}
              >
                {item.level > 1 && <ChevronRight size={14} style={{ marginRight: 5, opacity: 0.7 }} />}
                <span className="toc-text">{item.title}</span>
              </button>
            ))}
          </div>
        </nav>
      )}

      <article
        className="article-content"
        dangerouslySetInnerHTML={{ __html: processedContent }}
      />
    </div>
  );
};

export default BlogDetail;